import { Video } from 'api';
import { truncateBySentences } from 'lib/util';
import { filter, isEmpty, isNil, map, orderBy, slice } from 'lodash-es';

/**
 * Normalizes a timestamp to milliseconds, handling both string and number inputs.
 * If the timestamp is in seconds (10 digits), converts it to milliseconds.
 *
 * This normalization is needed because Sailthru sends timestamps in both string and number formats,
 * requiring us to handle both cases consistently.
 *
 * @param timestamp - The timestamp to normalize, either as a string or number
 * @returns The normalized timestamp in milliseconds
 */
const normalizeTimestamp = (timestamp: number | string): number => {
  const numericTimestamp =
    typeof timestamp === 'string' ? parseInt(timestamp) : timestamp;

  const isSeconds = numericTimestamp.toString().length === 10;
  const milliseconds = isSeconds ? numericTimestamp * 1000 : numericTimestamp;

  return milliseconds;
};

export enum SailthruSections {
  Recommendations = 'Recommendations',
  RecommendationsAI = 'RecommendationsAI',
  RecommendationsPsychology = 'RecommendationsPsychology',
  RecommendationsTEDEd = 'RecommendationsTEDEd',
  RecommendationsPopular = 'RecommendationsPopular',
  RecommendationsCountdown = 'RecommendationsCountdown',
  RecommendationsPodcast = 'RecommendationsPodcast',
  RecommendationsTrending = 'RecommendationsTrending',
  RecommendationsNewest = 'RecommendationsNewest'
}

export const sections = {
  [SailthruSections.Recommendations]: '9b4e837a-8318-11ee-911b-6a096f159873',
  [SailthruSections.RecommendationsAI]: '15375c30-b1bb-11ef-95a6-a2beda6870dc',
  [SailthruSections.RecommendationsPsychology]:
    '3a78a186-30b3-11ef-9854-9e7797660639',
  [SailthruSections.RecommendationsTEDEd]:
    '8fe0ecb2-b1bb-11ef-9465-42b3bfa427b2',
  [SailthruSections.RecommendationsPopular]:
    '932421fe-b1b2-11ef-8365-5a69f29232d2',
  [SailthruSections.RecommendationsCountdown]:
    'b3c7842e-b1bb-11ef-8365-5a69f29232d2',
  [SailthruSections.RecommendationsPodcast]:
    'ebc3e198-502b-11ef-a405-0273a0c42201',
  [SailthruSections.RecommendationsTrending]:
    'e499d75a-b1bb-11ef-95a6-a2beda6870dc',
  [SailthruSections.RecommendationsNewest]:
    '4b17e0d0-d3ef-11ef-af35-eacb2f1c3c9a'
};

type RecommendationVideo = Partial<Video> & {
  title: string;
  url: string;
  description: string;
  id: string;
};

export type Recommendation = {
  video: RecommendationVideo;
};

type SailthruItem = {
  url: string;
  title: string;
  description: string;
  views: number;
  date: string;
  tags: string[];
  image: string;
  vars: {
    duration: number;
    presenter_display_name: string;
    filmed_date: string;
    social_description: string;
    talk_type: string;
    talk_context: string;
    curator_approved: boolean;
  };
};

export type SailthruFormattedItem = {
  id: string;
  title: string;
  description: string;
  canonicalUrl: string;
  duration: number;
  publishedAt: number;
  presenterDisplayName: string;
  topics: {
    nodes: {
      name: string;
    }[];
  };
  primaryImageSet: [
    {
      url: string;
      aspectRatioName: string;
    }
  ];
  slug: string;
  type: {
    name: string;
  };
};

export type SailthruDataContentTypeSpecific = {
  [key in SailthruSections]: {
    id: string;
    title: string;
    description: string;
  }[];
};

export type SailthruDataContentType = {
  [key in SailthruSections]: SailthruFormattedItem[];
};

/**
 * Formats Sailthru data into a SailthruFormattedItem array.
 *
 * @param data - Array of SailthruItem objects to format
 * @returns Array of formatted SailthruFormattedItem objects
 */
export const formatSailthruData = (
  data: SailthruItem[]
): SailthruFormattedItem[] => {
  if (isEmpty(data)) return [];

  const formattedData = map(data, item => {
    if (!item?.url || isNil(item)) return null;

    const { title, url, description } = item;
    const formattedDescription = truncateBySentences(description, 2);
    const urlParts = url.split('/');
    const id = urlParts[urlParts.length - 1];

    return {
      id,
      title,
      description: formattedDescription,
      canonicalUrl: url,
      publishedAt: normalizeTimestamp(item.date),
      duration: item.vars?.duration * 60,
      presenterDisplayName: item.vars?.presenter_display_name,
      topics: {
        nodes: {
          ...map(item?.tags, tag => ({ name: tag }))
        }
      },
      primaryImageSet: [
        {
          url: item.image,
          aspectRatioName: '16:9'
        }
      ],
      slug: id,
      type: {
        name: item.vars?.talk_type
      }
    };
  });

  return orderBy(
    filter(formattedData, Boolean),
    ['publishedAt'],
    ['desc']
  ) as SailthruFormattedItem[];
};

/**
 * Fetches Sailthru data for all sections and formats it into a SailthruDataContentType object.
 *
 * @returns Promise<SailthruDataContentType> Object containing Sailthru data for each section
 *
 * @example
 * ```ts
 * const sailthruData = await fetchSailthruData();
 * ```
 */
export const fetchSailthruData = async (): Promise<SailthruDataContentType> => {
  const data = Object.values(SailthruSections).map(async sectionKey => {
    const sectionId = sections[sectionKey];
    const response = await fetch(
      `https://api.sail-personalize.com/v1/personalize?sections=${sectionId}`,
      {
        headers: {
          Authorization: `Bearer ${process.env.SAILTHRU_KEY}`
        }
      }
    );
    const data = await response.json();
    const recommendations =
      data?.sections?.[sectionId].json &&
      JSON.parse(data?.sections?.[sectionId].json);

    const formattedData = formatSailthruData(recommendations);
    return { sectionKey, data: formattedData };
  });

  const resolvedData = await Promise.all(data);

  const responseData = {
    [SailthruSections.Recommendations]: [],
    [SailthruSections.RecommendationsAI]: [],
    [SailthruSections.RecommendationsPsychology]: [],
    [SailthruSections.RecommendationsTEDEd]: [],
    [SailthruSections.RecommendationsPopular]: [],
    [SailthruSections.RecommendationsCountdown]: [],
    [SailthruSections.RecommendationsPodcast]: [],
    [SailthruSections.RecommendationsTrending]: [],
    [SailthruSections.RecommendationsNewest]: [],
    ...resolvedData.reduce<SailthruDataContentType>(
      (acc, { sectionKey, data }) => {
        acc[sectionKey] = data;
        return acc;
      },
      {} as SailthruDataContentType
    )
  };

  return responseData;
};

export const orderSailthruContent = (
  content: SailthruFormattedItem[],
  limit: number = 4
) => {
  return slice(orderBy(content, ['publishedAt'], ['desc']), 0, limit);
};
